import { formatDate } from 'utils';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { getOrgs } from 'redux/actions/Org';
import { fetchSettings } from 'redux/actions/Settings';
import { pageWithPayloadAndQuery } from 'redux/actions/Page';
import Request from 'lib/api/Request';
import { Org } from 'lib/models/Org';
import { User } from 'lib/models/User';
import { Location } from 'lib/models/Location';

import SimpleTable from 'views/Components/SimpleTable';
import { OrganizationComponent, EditOrgDialog } from './OrganizationPageView';
import { LocationsComponent } from './LocationsPageView';
import UsersPageView2 from './UsersPageView2';
import { roleConstantsSelector } from '../../selectors/roleConstantsSelector';

import { makeStyles } from '@material-ui/core/styles';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DetailsIcon from '@material-ui/icons/ZoomIn';


const DATE_FORMAT_STR = 'YYYY-MM-DD HH:mm:ss z';

const useStyles = makeStyles((theme) => ({
  specialDate: {
    fontStyle: 'italic',
  },
}));

const NeverLoggedIn = (props) => {
  const classes = useStyles();
  return <span className={classes.specialDate}>Never</span>
};

const NoUsers = (props) => {
  const classes = useStyles();
  return <span className={classes.specialDate}>No Users</span>
};

const FETCHING_STATE = {
  isFetching: true,
  error: null,
  data: [],
};

const setLoadStateWhenDone = (requestPromise, setLoadState) => {
  requestPromise.then(
    (data) => {
      setLoadState({
        isFetching: false,
        error: null,
        data,
      });
    }
  ).catch(
    (err) => {
      setLoadState({
        isFetching: false,
        error: 'Failed to get objects',
        data: [],
      });
    }
  );
};

const OrganizationDetail = (props) => {
  const [locationsLoadState, setLocationsLoadState] = useState(FETCHING_STATE);

  const {
    timezones, orgs, isFetching, orgsError, handleDeleteDone, orgId, routeToPage
  } = props;

  const fetchLocations = useCallback(() => {
    setLocationsLoadState(FETCHING_STATE);
    // Can't use redux fetchLocationsForOrg because it tramples over axios headers
    const request = new Request('/location', [{field:'orgId', value:orgId}]);
    setLoadStateWhenDone(request.get(), setLocationsLoadState);
  }, [orgId]);

  // let org;
  // if (!isFetching && !orgsError) {
  //   org = orgs.find(org => org.id === orgId);
  // }

  // fetches all the orgLogTypes
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  return (
  <div className='organizations-detail-page'>
    <div className='dashboard-table'>
      <OrganizationComponent
          orgs={orgs}
          isFetching={isFetching}
          orgsError={orgsError}
          orgId={orgId}
          getOrgs={() => null}
          timezones={timezones}
          handleDeleteDone={handleDeleteDone}
          sessionOrgId={orgId}
          superadmin={!!props.superadmin}
        />
    </div>

    <div className='dashboard-table'>
      <LocationsComponent
          locations={locationsLoadState.data}
          isFetching={locationsLoadState.isFetching}
          orgId={orgId}
          timezones={timezones}
          fetchLocationsForOrg={fetchLocations}
        />
    </div>

    <div className='dashboard-table'>
      <UsersPageView2
        locations={locationsLoadState.data}
        routeToPage={routeToPage}
      />
    </div>
  </div>
  );
};


const OrganizationsPageView = (props) => {
  const [openForm, setOpenForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedOrgModel, setSelectedOrgModel] = useState(false);
  const [locationModel, setLocationModel] = useState(false);
  const [userModel, setUserModel] = useState(false);

  const {
    currentUserId, timezones, user, orgs, isFetching, orgsError, getOrgs, 
    getOrgsAndSettings, gotoOrg, payload, sessionOrgId, roleConstants, 
    locations, routeToPage
  } = props;

  const renderLastLogin = (model) => {
    const persons = model.persons;
    if (!persons || persons.length === 0) {
      return <NoUsers/>;
    }
    const lastLogins = persons.map(person => person.lastLoginAt)
        .filter(lastLogin => lastLogin);
    if (lastLogins.length === 0) {
      return <NeverLoggedIn/>;
    }
    const maxLogin = lastLogins
        .reduce((accum, lastLogin) => accum > lastLogin ? accum : lastLogin);
    return formatDate({date: maxLogin, formatStr: DATE_FORMAT_STR, guessTz: true});
  }

  const tableColumns = [
    {
      title: 'Name',
      field: 'name',
      searchable: true,
    },
    {
      title: 'Short Name',
      field: 'shortName',
      searchable: true,
    },
    {
      title: 'Web Domain',
      field: 'webUrl',
      searchable: true,
    },
    {
      title: 'Last Login',
      field: 'lastLoginAt',
      searchable: false,
      renderValue: (column, model) => renderLastLogin(model),
    },
  ];

  const handleDeleteDone = () => {
    getOrgsAndSettings();
    gotoOrg({payload, orgId: undefined});
  };

  // fetches all the orgs
  useEffect(() => {
    if (sessionOrgId !== 'default') {
      getOrgs();
    }
  }, [getOrgs, sessionOrgId]);

  if (payload.id1) {
    return <OrganizationDetail
            props={props}
            orgId={payload.id1}
            orgs={orgs}
            isFetching={isFetching}
            orgsError={orgsError}
            timezones={timezones}
            currentUserId={currentUserId}
            user={user}
            handleDeleteDone={handleDeleteDone}
            roleConstants={roleConstants}
            locations={locations}
            routeToPage={routeToPage}
            superadmin={props.superadmin}
      />;
  }

  const handleClose = () => {
    setOpenForm(false)
    setIsSaving(false)
  }

  const handleSaveClick = async () => {
    setIsSaving(true)

    let existingUserJSON

    // query for users with entered email
    await new User({ email: userModel.email }).read().then(user => {
      if(user && user[0])
        existingUserJSON = user[0]
    })

    selectedOrgModel.create()
    .then((createdOrg = {}) => {
      const orgObjectForUser = { created: createdOrg.created, id: createdOrg.id, name: createdOrg.name }
      // after org creation we have necessary
      // data to successfully create first location
      locationModel.set({ orgId: createdOrg.id })
      locationModel.create()
      .then((createdLocation = {}) => {
        let method = 'create'

        // if this user exists, update instead of create
        if(existingUserJSON){
          method = 'update'
          userModel.set({ id: existingUserJSON.id })
        }

        // after location creation we have necessary
        // data to successfully create first user
        userModel.set({
          orgRoles: existingUserJSON ? [...existingUserJSON.orgRoles, { orgId: orgObjectForUser.id, roleId: 10 }, { orgId: orgObjectForUser.id, roleId: 40 }]
            : [{ orgId: orgObjectForUser.id, roleId: 10 }, { orgId: orgObjectForUser.id, roleId: 40 }], // default to administrator, responder
          locations: existingUserJSON ? [...existingUserJSON.locations, { orgId: createdOrg.id, id: createdLocation.id }]
            : [{ orgId: createdOrg.id, id: createdLocation.id }]
        })
        userModel[method]()
        .then(() => {
          getOrgsAndSettings()
          handleClose()
        })
      })
    })
    .catch(e => {
      handleClose()
    })
  }

  const handleTableRowClick = (model) => {
    gotoOrg({payload, orgId: model.id})
  }

  return (
    <div className='dashboard organizations-page'>
      <div className='dashboard-table'>
        <SimpleTable
          isNorthStar
          title="Organizations"
          data={orgs}
          isFetching={isFetching}
          rowClick={handleTableRowClick}
          error={orgsError}
          columns={tableColumns}
          toolbarActions={[
            {
              icon: AddCircleIcon,
              tooltip: 'Add New Organization',
              onClick: (event) => {
                setSelectedOrgModel(new Org())
                setLocationModel(new Location())
                setUserModel(new User({ orgRoles: [] })) // set empty list to clear log warnings for required prop
                setOpenForm(true)
              }
            }
          ]}
          actions={[
            {
              icon: DetailsIcon,
              tooltip: 'Show/Edit Details',
              onClick: (event, model) => {
                gotoOrg({payload, orgId: model.id});
              }
            }
          ]}
        />
      </div>

      <EditOrgDialog
        openForm={openForm}
        isSaving={isSaving}
        timezones={timezones}
        userModel={userModel}
        handleClose={handleClose}
        locationModel={locationModel}
        isSuperadmin={props.superadmin}
        selectedOrgModel={selectedOrgModel}
        handleSaveClick={() => handleSaveClick()}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    orgs = {},
    isFetching = true,
    error,
  } = state.org.listOrgs;

  const roleConstants = roleConstantsSelector(state);

  return {
    orgs,
    isFetching,
    orgsError: error,
    sessionOrgId: state.page.payload.orgId,
    roleConstants,
    user: state.session.settings.user,
    currentUser: state.session.settings.user,
    currentUserId: state.session.settings.user.id,
    superadmin: state.session.settings.user.superadmin,
    timezones: state.session.settings.accountConstants.timezones
  };
};

const mapDispatchToProps = dispatch => ({
  getOrgs: () => dispatch(getOrgs()),
  getOrgsAndSettings: () => {
      return dispatch(fetchSettings())
          .then(() => dispatch(getOrgs()));
    },
  gotoOrg: ({ payload, orgId }) => {
      dispatch(pageWithPayloadAndQuery({
        payload: {...payload, id1: orgId},
        query: {},
      }));
    },
});

OrganizationsPageView.propTypes = {
  orgs: PropTypes.array,
  payload: PropTypes.shape({}),
};

OrganizationsPageView.defaultProps = {
  orgs: [],
  isFetching: true,
  orgsError: null,
  payload: { toplevel: 'settings', secondlevel: 'organizations' },
};

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationsPageView);